import axios, { AxiosError } from "axios";
import {
	db,
	toaster,
	IModel,
	DBType,
	isIdentifiable,
	IDeleteRestResponse,
	isDeleteRestResponse,
	IErrorRestResponse,
	InsertOrUpdatePayload,
	InsertOrUpdateManyPayload,
	DEBUG,
} from "@/loader";

export interface RestCollectionParamAdvanced<T> {
	values: T | T[];
	operator?: "" | "=" | "!=" | "<" | ">" | "<=" | ">=" | "IN" | "NOT IN" | "LIKE" | "NOT LIKE" | "BETWEEN" | "NOT BETWEEN" | "IS NULL" | "IS NOT NULL" | null;
	relation?: "" | "AND" | "OR" | null;
	type?: "" | "date" | "datetime" | "time" | "timestamp" | "bit" | "bool" | "boolean" | "int" | "tinyint" | "smallint" | "mediumint" | "bigint" | "integer" | "long" | "year" | "double" | "float" | "decimal" | null;
}

export type RestCollectionOrder = "ASC" | "DESC";

export type RestCollectionParam<T> = T | T[] | RestCollectionParamAdvanced<T>;

export interface IRestParams {
	context?: "view" | "edit" | "embed";
}

export interface IRestDeleteParams{
	force?: boolean;
}

export interface IRestCollectionParams extends IRestParams {
	page?: number;
	per_page?: number;
	search?: string;
	orderby?: string | string[];
	order?: RestCollectionOrder | RestCollectionOrder[];
}

export interface IRestResult<T> {
	success: boolean;
	item?: T;
	items?: T[];
	total?: number;
	error?: IErrorRestResponse;
}

/* class decorator */
export function staticImplements<T>() {
	return <U extends T>(constructor: U) => { constructor; };
}

export interface IEntityRest<I, T extends IModel<I>> {
	getURL(data: I | T | undefined, singular: boolean): string;
	// list(params?: IRestCollectionParams): Promise<IRestResult<T>>;
	get(data: I | T, params?: IRestParams): Promise<IRestResult<T>>;
	create(data: I | T): Promise<IRestResult<T>>;
	update(data: I | T): Promise<IRestResult<T>>;
	delete(data: I | T, params?: IRestDeleteParams): Promise<IRestResult<T>>;
}

function errorHandler(error: AxiosError<IErrorRestResponse>): IRestResult<any> {
	let result = error.response?.data;
	if (DEBUG) {
		console.log(result);
	}
	toaster.toast({ message: result?.message || "", variant: [401, 403, 404].includes(result?.data.status) ? "warning" : "danger" });
	return { success: false, error: result };
}

export abstract class RestAPI {
	public static url: string = "/wp-json";
	public static readonly namespace: string = "vip/v1";
	public static readonly wpNamespace: string = "wp/v2";

	public static async list<I, T extends IModel<I>>(url: string, type: DBType, params?: IRestCollectionParams): Promise<IRestResult<T>> {
		return await axios.get<I[]>(url, { params }).then(response => {
			let payload: InsertOrUpdateManyPayload<I, T> = { items: response.data, type };
			db.insertOrUpdateMany(payload);
			let total = Number.orDefault(response.headers?.["x-wp-total"]);
			return <IRestResult<T>>{ success: true, items: payload.result || [], total };
		}).catch(errorHandler);
	}

	public static async get<I, T extends IModel<I>>(url: string, type: DBType, params?: IRestParams): Promise<IRestResult<T>> {
		return await axios.get<I>(url, { params }).then(response => {
			let payload: InsertOrUpdatePayload<I, T> = { item: response.data, type };
			db.insertOrUpdate(payload);
			return <IRestResult<T>>{ success: true, item: payload.result };
		}).catch(errorHandler);
	}

	public static async post<I, T extends IModel<I>>(url: string, data: I | T, type: DBType, method: "POST" | "PUT" | "PATCH" = "POST"): Promise<IRestResult<T>> {
		const obj = data;
		data = { ...data };
		if (isIdentifiable(data)) {
			// @ts-ignore
			if (data.id < 0) {
				// @ts-ignore
				data.id = undefined;
			}
		}
		// @ts-ignore
		if ("password" in data && !data.password) {
			// @ts-ignore
			data.password = undefined;
		}

		return await axios.request<I>({
			url,
			method,
			data,
		}).then(response => {
			if (isIdentifiable(obj)) {
				// @ts-ignore
				if ("update" in obj && typeof obj.update === "function") {
					// @ts-ignore
					obj.update(response.data);
					return <IRestResult<T>>{ success: true, item: obj };
				} else {
					// @ts-ignore
					obj.id = response.data.id;
				}
			}
			let payload: InsertOrUpdatePayload<I, T> = { item: response.data, type };
			db.insertOrUpdate(payload);
			return <IRestResult<T>>{ success: true, item: payload.result };
		}).catch(errorHandler);
	}

	public static async put<I, T extends IModel<I>>(url: string, data: I | T, type: DBType): Promise<IRestResult<T>> {
		return await RestAPI.post<I, T>(url, data, type, "PUT");
	}

	public static async patch<I, T extends IModel<I>>(url: string, data: I | T, type: DBType): Promise<IRestResult<T>> {
		return await RestAPI.post<I, T>(url, data, type, "PATCH");
	}

	public static async delete<I, T extends IModel<I>>(url: string, type: DBType, data?: IRestDeleteParams): Promise<IRestResult<T>> {
		return await axios.delete<I | IDeleteRestResponse<I>>(url, { data })
			.then(response => {
				if (isDeleteRestResponse(response.data)) {
					// The object has been deleted from the database
					if (response.data.deleted) {
						db.remove({ item: response.data.previous, type, force: true });
					}
					return <IRestResult<T>>{ success: response.data.deleted };
				} else {
					// The object has been marked as trashed in the database
					let payload: InsertOrUpdatePayload<I, T> = { item: response.data, type };
					db.insertOrUpdate(payload);
					return <IRestResult<T>>{ success: true, item: payload.result };
				}
			}).catch(errorHandler);
	}
}
